import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { storeAPI } from '../../services/api';
import { useCart } from '../../context/CartContext';
import { ShoppingBag, ArrowRight, Star, Truck, Shield, Sparkles, ChevronRight } from 'lucide-react';
import ProductCard from '../../components/store/ProductCard';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};
const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 200, damping: 25 } }
};

const StoreHome = () => {
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [catRes, featRes, packRes] = await Promise.all([
          storeAPI.getCategories(),
          storeAPI.getFeaturedProducts(8),
          storeAPI.getPacks(),
        ]);
        setCategories(catRes.data);
        setFeatured(featRes.data);
        setPacks(packRes.data);
      } catch (err) {
        console.error('Error loading store:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleAddToCart = async (product) => {
    try {
      await addItem(product.id);
    } catch (err) {
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
          className="w-12 h-12 border-4 border-orange-200 border-t-orange-600 rounded-full"
        />
      </div>
    );
  }

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
        <div className="absolute inset-0 opacity-30">
          <motion.div
            animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
            transition={{ repeat: Infinity, duration: 8, ease: "easeInOut" }}
            className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl"
          />
          <motion.div
            animate={{ x: [0, -20, 0], y: [0, 30, 0] }}
            transition={{ repeat: Infinity, duration: 10, ease: "easeInOut" }}
            className="absolute bottom-10 right-10 w-96 h-96 bg-orange-600 rounded-full mix-blend-multiply filter blur-3xl"
          />
          <motion.div
            animate={{ x: [0, 20, 0], y: [0, 15, 0] }}
            transition={{ repeat: Infinity, duration: 7, ease: "easeInOut" }}
            className="absolute top-1/2 left-1/3 w-64 h-64 bg-orange-400 rounded-full mix-blend-multiply filter blur-3xl opacity-60"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 py-28 relative">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-sm font-medium mb-6"
            >
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="w-2 h-2 bg-orange-500 rounded-full"
              />
              Plataforma Comercial STTORA
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-5xl md:text-6xl font-bold mb-6 leading-tight"
            >
              Cambiando vidas <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600">
                y haciendo historia
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="text-xl text-gray-300 mb-10 leading-relaxed"
            >
              Descubre productos de consumo masivo, digitales y el programa de
              recompensas multinivel más innovador.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="flex gap-4 flex-wrap"
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/store/categories"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-3.5 rounded-xl font-semibold hover:from-orange-600 hover:to-orange-700 shadow-xl shadow-orange-500/30 transition-all"
                >
                  <ShoppingBag className="w-5 h-5" />
                  Comprar Ahora
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 border border-gray-600 text-gray-300 px-8 py-3.5 rounded-xl font-semibold hover:border-orange-500 hover:text-orange-400 transition-all"
                >
                  Registrarse Gratis
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <motion.section
        variants={itemVariants}
        className="max-w-7xl mx-auto px-4 -mt-14 mb-20"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Truck, title: 'Envío Rastreable', desc: 'Actualización manual de estado', color: 'from-blue-500 to-cyan-500' },
            { icon: Sparkles, title: 'Sttora Points', desc: 'Acumula y canjea puntos', color: 'from-orange-500 to-amber-500' },
            { icon: Shield, title: 'Calidad Garantizada', desc: 'Productos 100% originales', color: 'from-green-500 to-emerald-500' },
          ].map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              whileHover={{ y: -6 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl p-6 flex items-center gap-4 border border-gray-100 dark:border-gray-700 transition-all"
            >
              <div className={`p-4 rounded-xl bg-gradient-to-br ${item.color} shadow-lg shrink-0`}>
                <item.icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{item.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {featured.length > 0 && (
        <motion.section variants={itemVariants} className="max-w-7xl mx-auto px-4 mb-20">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Productos Destacados</h2>
              <p className="text-gray-500 dark:text-gray-400 mt-1">Los más populares de la semana</p>
            </div>
            <motion.div whileHover={{ x: 5 }}>
              <Link to="/store/featured" className="flex items-center gap-1 text-orange-500 hover:text-orange-600 font-medium text-sm">
                Ver todos <ChevronRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {featured.map((product) => (
              <ProductCard key={product.id} product={product} onAddToCart={handleAddToCart} />
            ))}
          </div>
        </motion.section>
      )}

      {packs.length > 0 && (
        <motion.section variants={itemVariants} className="max-w-7xl mx-auto px-4 mb-20">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Sttora Packs</h2>
              <p className="text-gray-500 dark:text-gray-400 mt-1">Combos especiales ahorrando más</p>
            </div>
            <motion.div whileHover={{ x: 5 }}>
              <Link to="/store/packs" className="flex items-center gap-1 text-purple-500 hover:text-purple-600 font-medium text-sm">
                Ver todos <ChevronRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {packs.slice(0, 3).map(pack => (
              <Link key={pack.id} to={`/store/packs/${pack.id}`}
                className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 hover:shadow-lg hover:border-purple-300 dark:hover:border-purple-700 transition-all group"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 shadow-lg shrink-0">
                    <ShoppingBag className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white group-hover:text-purple-600 transition-colors">{pack.name}</h3>
                </div>
                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">${pack.price?.toFixed(2)}</p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {(pack.items || []).slice(0, 3).map((item, i) => (
                    <span key={i} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-lg text-xs">{item.product_name || `Item ${i+1}`}</span>
                  ))}
                  {(pack.items || []).length > 3 && <span className="text-xs text-gray-400">+{pack.items.length - 3} más</span>}
                </div>
              </Link>
            ))}
          </div>
        </motion.section>
      )}

      <motion.section variants={itemVariants} className="max-w-7xl mx-auto px-4 mb-20">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Categorías</h2>
            <p className="text-gray-500 dark:text-gray-400 mt-1">Explora por categoría</p>
          </div>
          <motion.div whileHover={{ x: 5 }}>
            <Link to="/store/categories" className="flex items-center gap-1 text-orange-500 hover:text-orange-600 font-medium text-sm">
              Ver todas <ChevronRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {categories.slice(0, 15).map((cat, i) => (
            <motion.div
              key={cat.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              whileHover={{ y: -4 }}
            >
              <Link
                to={`/store/categories/${cat.slug}`}
                className="block bg-white dark:bg-gray-800 rounded-xl p-5 text-center border border-gray-100 dark:border-gray-700 hover:shadow-lg hover:border-orange-300 dark:hover:border-orange-700 transition-all group"
              >
                <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-orange-100 to-orange-200 dark:from-orange-900/50 dark:to-orange-800/50 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <ShoppingBag className="w-6 h-6 text-orange-500" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">{cat.name}</h3>
                <span className={`text-xs mt-1 inline-block px-2 py-0.5 rounded-full ${cat.is_physical ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'}`}>
                  {cat.is_physical ? 'Físico' : 'Digital'}
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.section>
    </motion.div>
  );
};

export default StoreHome;
